import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useTimeContext } from "./DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Send, Sparkles, User, Trash2, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const cleanMd = (t) => t ? t.replace(/\*\*/g, '').replace(/\*/g, '').replace(/#{1,3}\s/g, '') : t;
const SUGGESTIONS = [
  "What are our top performing projects?",
  "Why did cancellations increase?",
  "How is collection efficiency this period?",
  "Where are we underperforming against targets?",
  "What should we focus on next quarter?",
  "Show sales trend analysis",
];

export default function AIAssistant() {
  const { period, refDate, token } = useTimeContext();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: "user", content: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post(`${API}/ai/copilot`, {
        message: text.trim(),
        session_id: sessionId,
        period,
        ref_date: refDate,
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (!sessionId) setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: "assistant", content: "I'm unable to process that request right now. Please try again." }]);
    } finally {
      setLoading(false);
    }
  }, [loading, sessionId, period, refDate, token]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const clearChat = async () => {
    if (sessionId) {
      try {
        await axios.delete(`${API}/ai/copilot/history?session_id=${sessionId}`, { headers: { Authorization: `Bearer ${token}` } });
      } catch (e) { /* ignore */ }
    }
    setMessages([]);
    setSessionId(null);
  };

  const isEmpty = messages.length === 0;

  return (
    <div className="flex flex-col h-[calc(100dvh-180px)] sm:h-[calc(100dvh-120px)]" data-testid="ai-assistant-page">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>Nexus AI</h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">AI Business Analyst — understands your data, explains why, suggests what next</p>
          </div>
          {messages.length > 0 && (
            <button onClick={clearChat} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-testid="clear-chat-btn">
              <Trash2 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Clear</span>
            </button>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <Card className="flex-1 flex flex-col min-h-0 bg-white rounded-2xl border-slate-200/60 shadow-[0_2px_10px_rgba(0,0,0,0.02)]" data-testid="copilot-chat-card">
        <div className="flex-1 overflow-y-auto p-3 sm:p-5" data-testid="copilot-messages">
          {isEmpty ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <div className="h-14 w-14 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
                <Sparkles className="h-7 w-7 text-blue-600" />
              </div>
              <h2 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope' }}>Nexus AI</h2>
              <p className="text-sm text-slate-500 mb-6 max-w-md">
                Ask me anything about your business data. I analyze your sales, collections, targets, and projects to give you actionable intelligence.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-lg">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => sendMessage(s)} className="text-left px-3 py-2.5 text-xs sm:text-sm text-slate-600 bg-slate-50 hover:bg-blue-50 hover:text-blue-700 rounded-xl border border-slate-200 hover:border-blue-200 transition-all" data-testid={`suggestion-${i}`}>
                    <MessageSquare className="h-3 w-3 inline mr-1.5 text-slate-400" />{s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, i) => (
                <div key={i} className={`flex gap-2.5 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${msg.role}-${i}`}>
                  {msg.role === 'assistant' && (
                    <div className="h-7 w-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Sparkles className="h-3.5 w-3.5 text-blue-600" />
                    </div>
                  )}
                  <div className={`max-w-[85%] sm:max-w-[75%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-[#1D4ED8] text-white rounded-br-md'
                      : 'bg-slate-50 text-slate-800 border border-slate-200 rounded-bl-md'
                  }`}>
                    {msg.role === 'assistant' ? (
                      <div className="copilot-response whitespace-pre-wrap">{cleanMd(msg.content)}</div>
                    ) : msg.content}
                  </div>
                  {msg.role === 'user' && (
                    <div className="h-7 w-7 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <User className="h-3.5 w-3.5 text-slate-600" />
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex gap-2.5 justify-start">
                  <div className="h-7 w-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-3.5 w-3.5 text-blue-600 animate-pulse" />
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <div className="h-2 w-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="h-2 w-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="h-2 w-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input */}
        <div className="border-t border-slate-100 p-3 sm:p-4">
          <form onSubmit={handleSubmit} className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about sales, targets, projects..."
              className="flex-1 h-10 sm:h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 transition-all"
              disabled={loading}
              data-testid="copilot-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="h-10 sm:h-11 w-10 sm:w-11 flex items-center justify-center bg-[#1D4ED8] hover:bg-[#1E40AF] text-white rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              data-testid="copilot-send-btn"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
          <p className="text-[9px] text-slate-400 mt-1.5 text-center">
            Nexus AI uses your live data for {period === 'as_on_date' ? 'As On Date' : period.toUpperCase()} period
          </p>
        </div>
      </Card>
    </div>
  );
}
